import {
  SourceType,
  type SourcesV2,
} from "@/components/PowerBlock/Block/SourcesField/SelectSource";
import { FieldType } from "@/components/PowerBlock/Block/types/fieldTypes";
import { TemplateFormat } from "@/types/prompt-blueprint";

export interface InputVariablesFieldProps {
  variables: string[];
  sources: Record<SourceType, string[]>;
  sourceKeyPrefix: string;
  isLoading?: boolean;
  showAutofill?: boolean;
  onAutofill?: (mappings: Record<string, string>) => void;
  fieldType?: FieldType;
  templateFormat?: TemplateFormat;
  forceOpenVariableSelector?: string;
}

export interface AutofillButtonProps {
  variables: string[];
  sources: Record<SourceType, string[]>;
  sourcesV2?: SourcesV2 | null;
  onAutofill: (mappings: Record<string, string>) => void;
  disabled?: boolean;
  isLoading?: boolean;
  mappableCount?: number;
  readonly?: boolean;
}

export interface AutofillMatch {
  variableName: string;
  sourceKey: string;
  matchType: "exact" | "partial" | "fuzzy";
  confidence: number;
}

export interface AutofillResult {
  mappings: Record<string, string>;
  matches: AutofillMatch[];
  totalPossible: number;
  totalMapped: number;
}

export interface AutofillUtilities {
  findBestMatch: (variableName: string, availableSources: string[]) => AutofillMatch | null;
  generateAutoMappings: (
    unmappedVariables: string[],
    sources: Record<SourceType, string[]>,
    sourcesV2?: SourcesV2 | null,
  ) => AutofillResult;
  getAllSourceKeys: (
    sources: Record<SourceType, string[]>,
    sourcesV2?: SourcesV2 | null,
  ) => string[];
  hasAutofillSuggestions: (
    unmappedVariables: string[],
    sources: Record<SourceType, string[]>,
    sourcesV2?: SourcesV2 | null,
  ) => boolean;
  getAutofillableCount: (
    unmappedVariables: string[],
    sources: Record<SourceType, string[]>,
    sourcesV2?: SourcesV2 | null,
  ) => number;
}

export const formatVariableName = (
  variableName: string,
  templateFormat?: TemplateFormat,
): string => {
  if (variableName.startsWith("{") && variableName.endsWith("}")) {
    if (templateFormat === "jinja2" && !variableName.startsWith("{{")) {
      return `{${variableName}}`;
    }
    if (templateFormat === "f-string" && variableName.startsWith("{{")) {
      return variableName.slice(1, -1);
    }
    return variableName;
  }

  switch (templateFormat) {
    case "jinja2":
      return `{{${variableName}}}`;
    case "f-string":
    default:
      return `{${variableName}}`;
  }
};
